import { useState, useMemo, useEffect, useRef } from 'react';
import { REPORT_FILTER_FIELDS } from '../../config/reportFilterFields';
import { REPORT_METRICS } from '../../config/reportMetrics';
import type { FilterCondition, FilterFieldDefinition, FilterFieldCategory } from '../../types/reportTypes';
import '../../css/FilterBuilderModal.css';

interface FilterBuilderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (condition: FilterCondition) => void;
    selectedMetricKey?: string;
    companyId: string;
}

const CATEGORY_LABELS: Record<FilterFieldCategory, string> = {
    asset: 'Asset',
    organization: 'Organization',
    financial: 'Financial',
    pm: 'PM',
    tire: 'Tire',
    safety: 'Safety',
    system: 'System',
};

const CATEGORY_ORDER: FilterFieldCategory[] = [
    'asset', 'organization', 'financial', 'pm', 'tire', 'safety', 'system',
];

/**
 * Modal overlay with two-panel field selection for building filter conditions.
 * Left panel shows categories, right panel shows fields within selected category.
 * Condition operators change by field type; value input adapts by field.
 *
 * Validates: Requirements 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8
 */
export const FilterBuilderModal = ({
    isOpen,
    onClose,
    onApply,
    selectedMetricKey,
    companyId,
}: FilterBuilderModalProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeCategory, setActiveCategory] = useState<FilterFieldCategory>('asset');
    const [selectedField, setSelectedField] = useState<FilterFieldDefinition | null>(null);
    const [operator, setOperator] = useState('');
    const [value, setValue] = useState<string>('');
    const [value2, setValue2] = useState<string>('');
    const [dropdownOptions, setDropdownOptions] = useState<{ label: string; value: string }[]>([]);
    const [multiValues, setMultiValues] = useState<string[]>([]);
    const [booleanValue, setBooleanValue] = useState<string>('true');
    const overlayRef = useRef<HTMLDivElement>(null);

    // Determine which contextual filter fields are visible based on selected metric
    const visibleFields = useMemo(() => {
        const metric = REPORT_METRICS.find((m) => m.key === selectedMetricKey);
        const contextualKeys = new Set(metric?.contextualFilterFields ?? []);

        return REPORT_FILTER_FIELDS.filter((field) => {
            if (field.isBaseFilter) return true;
            if (contextualKeys.has(field.key)) return true;
            // Show all fields if no metric selected
            if (!selectedMetricKey) return true;
            return false;
        });
    }, [selectedMetricKey]);

    // Filter fields by search query
    const filteredFields = useMemo(() => {
        if (!searchQuery.trim()) return visibleFields;
        const q = searchQuery.toLowerCase();
        return visibleFields.filter((f) => f.label.toLowerCase().includes(q));
    }, [visibleFields, searchQuery]);

    // Group by category
    const fieldsByCategory = useMemo(() => {
        const map = new Map<FilterFieldCategory, FilterFieldDefinition[]>();
        for (const field of filteredFields) {
            const cat = field.category;
            if (!map.has(cat)) map.set(cat, []);
            map.get(cat)!.push(field);
        }
        return map;
    }, [filteredFields]);

    // Available categories (only those with fields)
    const availableCategories = useMemo(() => {
        return CATEGORY_ORDER.filter((cat) => fieldsByCategory.has(cat));
    }, [fieldsByCategory]);

    // Auto-select first available category
    useEffect(() => {
        if (availableCategories.length > 0 && !availableCategories.includes(activeCategory)) {
            setActiveCategory(availableCategories[0]);
        }
    }, [availableCategories, activeCategory]);

    // Load dropdown options when field changes
    useEffect(() => {
        if (selectedField?.optionsFn) {
            selectedField.optionsFn(companyId).then(setDropdownOptions).catch(() => setDropdownOptions([]));
        } else {
            setDropdownOptions([]);
        }
    }, [selectedField, companyId]);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setSearchQuery('');
            setSelectedField(null);
            setOperator('');
            setValue('');
            setValue2('');
            setMultiValues([]);
            setBooleanValue('true');
        }
    }, [isOpen]);

    const handleFieldSelect = (field: FilterFieldDefinition) => {
        setSelectedField(field);
        setOperator(field.operators[0] || '');
        setValue('');
        setValue2('');
        setMultiValues([]);
        setBooleanValue('true');
    };

    const handleApply = () => {
        if (!selectedField || !operator) return;

        let conditionValue: FilterCondition['value'];

        if (selectedField.fieldType === 'boolean') {
            conditionValue = booleanValue;
        } else if (operator === 'Between') {
            if (selectedField.fieldType === 'number') {
                conditionValue = [parseFloat(value) || 0, parseFloat(value2) || 0] as [number, number];
            } else {
                conditionValue = [value, value2] as [string, string];
            }
        } else if (operator === 'In' || operator === 'Not In') {
            conditionValue = multiValues.length > 0 ? multiValues : value.split(',').map((v) => v.trim()).filter(Boolean);
        } else if (selectedField.fieldType === 'number') {
            conditionValue = parseFloat(value) || 0;
        } else {
            conditionValue = value;
        }

        const condition: FilterCondition = {
            fieldKey: selectedField.key,
            fieldLabel: selectedField.label,
            fieldType: selectedField.fieldType === 'multi-select' || selectedField.fieldType === 'boolean'
                ? 'dropdown'
                : (selectedField.fieldType as 'text' | 'number' | 'date' | 'dropdown'),
            operator,
            value: conditionValue,
        };

        onApply(condition);
        onClose();
    };

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === overlayRef.current) onClose();
    };

    const handleMultiToggle = (val: string) => {
        setMultiValues((prev) =>
            prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
        );
    };

    const isApplyDisabled = !selectedField || !operator || (
        selectedField.fieldType !== 'boolean' &&
        operator !== 'In' && operator !== 'Not In' && !value
    ) || (
            (operator === 'In' || operator === 'Not In') && multiValues.length === 0 && !value
        );

    if (!isOpen) return null;

    const fieldsInCategory = fieldsByCategory.get(activeCategory) ?? [];

    return (
        <div className="filter-modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>
            <div className="filter-modal" role="dialog" aria-modal="true" aria-label="Add Filter">
                <div className="filter-modal-header">
                    <h3>Add Filter</h3>
                    <button className="filter-modal-close-btn" onClick={onClose} aria-label="Close">×</button>
                </div>

                <div className="filter-modal-search">
                    <input
                        type="text"
                        placeholder="Search fields..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label="Search filter fields"
                    />
                </div>

                <div className="filter-modal-body">
                    <div className="filter-modal-categories">
                        {availableCategories.map((cat) => (
                            <button
                                key={cat}
                                className={`filter-category-item ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {CATEGORY_LABELS[cat]}
                            </button>
                        ))}
                    </div>

                    <div className="filter-modal-fields">
                        {fieldsInCategory.length === 0 ? (
                            <div className="filter-no-fields">No fields match your search.</div>
                        ) : (
                            fieldsInCategory.map((field) => (
                                <button
                                    key={field.key}
                                    className={`filter-field-item ${selectedField?.key === field.key ? 'selected' : ''}`}
                                    onClick={() => handleFieldSelect(field)}
                                >
                                    {field.label}
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {selectedField && (
                    <div className="filter-condition-builder">
                        <div className="filter-condition-builder-label">
                            Condition for: {selectedField.label}
                        </div>
                        <div className="filter-condition-row">
                            <select
                                className="filter-condition-operator"
                                value={operator}
                                onChange={(e) => {
                                    setOperator(e.target.value);
                                    setValue('');
                                    setValue2('');
                                    setMultiValues([]);
                                }}
                                aria-label="Operator"
                            >
                                {selectedField.operators.map((op) => (
                                    <option key={op} value={op}>{op}</option>
                                ))}
                            </select>

                            {renderValueInput(
                                selectedField, operator, value, setValue, value2, setValue2,
                                dropdownOptions, multiValues, handleMultiToggle,
                                booleanValue, setBooleanValue,
                            )}
                        </div>
                    </div>
                )}

                <div className="filter-modal-footer">
                    <button className="filter-modal-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="filter-modal-apply-btn"
                        onClick={handleApply}
                        disabled={isApplyDisabled}
                    >
                        Apply Filter
                    </button>
                </div>
            </div>
        </div>
    );
};


// ─── Value Input Renderer ───────────────────────────────────────────────────

function renderValueInput(
    field: FilterFieldDefinition,
    operator: string,
    value: string,
    setValue: (v: string) => void,
    value2: string,
    setValue2: (v: string) => void,
    dropdownOptions: { label: string; value: string }[],
    multiValues: string[],
    handleMultiToggle: (val: string) => void,
    booleanValue: string,
    setBooleanValue: (v: string) => void,
) {
    // Boolean toggle
    if (field.fieldType === 'boolean') {
        return (
            <div className="filter-boolean-toggle">
                <label>
                    <input
                        type="radio"
                        name="boolFilter"
                        value="true"
                        checked={booleanValue === 'true'}
                        onChange={() => setBooleanValue('true')}
                    />
                    Yes
                </label>
                <label>
                    <input
                        type="radio"
                        name="boolFilter"
                        value="false"
                        checked={booleanValue === 'false'}
                        onChange={() => setBooleanValue('false')}
                    />
                    No
                </label>
            </div>
        );
    }

    // Between — two inputs
    if (operator === 'Between') {
        const inputType = field.fieldType === 'number' ? 'number' : field.fieldType === 'date' ? 'date' : 'text';
        return (
            <div className="filter-condition-between">
                <input
                    type={inputType}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="From"
                    aria-label="From value"
                />
                <span>and</span>
                <input
                    type={inputType}
                    value={value2}
                    onChange={(e) => setValue2(e.target.value)}
                    placeholder="To"
                    aria-label="To value"
                />
            </div>
        );
    }

    // In / Not In — multi-select checkboxes if options available
    if ((operator === 'In' || operator === 'Not In') && dropdownOptions.length > 0) {
        return (
            <div style={{ flex: 1, maxHeight: 120, overflowY: 'auto' }}>
                {dropdownOptions.map((opt) => (
                    <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 13, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={multiValues.includes(opt.value)}
                            onChange={() => handleMultiToggle(opt.value)}
                        />
                        {opt.label}
                    </label>
                ))}
            </div>
        );
    }

    // In Last X Days — numeric input
    if (operator === 'In Last X Days') {
        return (
            <input
                className="filter-condition-value"
                type="number"
                min="1"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Number of days"
                aria-label="Number of days"
            />
        );
    }

    // Date picker
    if (field.fieldType === 'date') {
        return (
            <input
                className="filter-condition-value"
                type="date"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-label="Date value"
            />
        );
    }

    // Numeric input
    if (field.fieldType === 'number') {
        return (
            <input
                className="filter-condition-value"
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Enter value"
                aria-label="Numeric value"
            />
        );
    }

    // Dropdown with options
    if (dropdownOptions.length > 0) {
        return (
            <select
                className="filter-condition-value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                aria-label="Select value"
            >
                <option value="">Select...</option>
                {dropdownOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        );
    }

    // Default text input
    return (
        <input
            className="filter-condition-value"
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Enter value"
            aria-label="Filter value"
        />
    );
}
